import {
  TableCellProps,
  WbButton,
  WbDrawer,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { Entity } from '@backstage/catalog-model';
import { Box, Switch, Typography, makeStyles } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import { FC, useEffect, useState } from 'react';
import { useAssignmentsContext } from './AssignmentsContext';

const useStyles = makeStyles(
  theme => ({
    flexBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  }),
  { name: 'EditEnabledScopesDrawer' },
);

export const EditEnabledScopesDrawer: FC = () => {
  const classes = useStyles();
  const {
    openEditEnabledScopesDrawer,
    setEditEnabledScopesDrawer,
    selectedRow,
    setSelectedRow,
    updateAssignments,
  } = useAssignmentsContext();

  const [scopes, setScopes] = useState(selectedRow?.scopes);

  useEffect(() => {
    if (selectedRow && openEditEnabledScopesDrawer) {
      setScopes(selectedRow?.scopes);
    } else {
      setScopes([]);
    }
  }, [openEditEnabledScopesDrawer, selectedRow]);

  const onChangeHandler = (row: any) => {
    setScopes(prevScopes =>
      prevScopes?.map(scope =>
        scope?.urn === row?.urn ? { ...scope, enabled: !scope.enabled } : scope,
      ),
    );
  };

  const columns: TableCellProps<{
    urn: string;
    entity?: Entity;
    enabled?: boolean;
  }>[] = [
    {
      field: 'urn',
      cellProps: {
        size: 'small',
        align: 'left',
        style: { wordBreak: 'break-word' },
      },
      headerName: 'scope',
      fieldRender: field => (
        <Typography variant="body2">
          {field.entity?.metadata.displayName ??
            (field.entity?.spec?.mesh as any)?.name ??
            field.entity?.metadata.name ??
            field?.urn}
        </Typography>
      ),
    },
    {
      field: 'entity',
      cellProps: {
        size: 'small',
        align: 'left',
        style: { wordBreak: 'break-word' },
      },
      headerName: 'type',
      fieldRender: field => (
        <Typography variant="body2">{field.entity?.kind ?? '-'}</Typography>
      ),
    },
    {
      field: 'enabled',
      cellProps: {
        size: 'small',
        align: 'right',
        width: '10%',
      },
      headerName: '',
      fieldRender: field => {
        return (
          <Switch
            disabled={field?.urn === '*'}
            checked={field.enabled}
            onChange={() => onChangeHandler(field)}
          />
        );
      },
    },
  ];

  return (
    <WbDrawer
      open={openEditEnabledScopesDrawer}
      onClose={() => setSelectedRow(undefined)}
      setOpen={setEditEnabledScopesDrawer}
      title="Scope settings"
      PaperProps={{
        style: {
          width: '40%',
        },
      }}
      contentContainerStyle={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}
      actions={
        <>
          <WbButton
            onClick={() => {
              setEditEnabledScopesDrawer(false);
              setSelectedRow(undefined);
            }}
            variant="outlined"
            color="primary"
            label="Cancel"
          />
          <WbButton
            disabled={selectedRow?.scopes?.[0]?.urn === '*'}
            onClick={() => {
              updateAssignments(
                selectedRow?.userOrGroup?.urn!,
                scopes?.map(s => ({
                  enabled: s.enabled ?? true,
                  urn: s?.urn,
                }))!,
              );
            }}
            label="Update"
            variant="contained"
            color="primary"
          />
        </>
      }
    >
      <Box className={classes.flexBox} marginBottom="8px">
        {selectedRow?.userOrGroup.entity ? (
          <>
            {selectedRow?.userOrGroup?.entity.kind === 'User' ? (
              <PersonIcon fontSize="small" />
            ) : (
              <GroupIcon fontSize="small" />
            )}
            <Typography variant="body1" style={{ fontWeight: '500' }}>
              {(selectedRow?.userOrGroup?.entity?.spec?.profile as any)
                ?.displayName ??
                selectedRow?.userOrGroup?.entity?.metadata.name ??
                selectedRow?.userOrGroup?.urn}
            </Typography>
          </>
        ) : (
          <Typography variant="body1" style={{ fontWeight: '500' }}>
            {selectedRow?.userOrGroup?.urn}
          </Typography>
        )}
      </Box>

      <WbTable<{ urn: string; entity?: Entity; enabled?: boolean }>
        styles={{
          container: { height: '100%', flex: '1 1 0' },
        }}
        components={{
          tableContent: {
            columns,
            rows: scopes,
          },
        }}
      />
    </WbDrawer>
  );
};
